import { useState, useEffect, useCallback, useRef } from 'react';
import { calculatePosition } from '../utils';

/**
 * useGanttToday Hook
 * 
 * Manages the "today" marker position and scroll-to-today functionality.
 * Updates position every minute to keep it accurate.
 * 
 * @param {Object} dateRange - { startDate, endDate, totalDays }
 * @param {React.RefObject} chartRef - Reference to the chart container
 * @returns {{ todayPosition, scrollToToday }}
 */
export function useGanttToday(dateRange, chartRef) {
  const [todayPosition, setTodayPosition] = useState(null);
  
  // Update today position initially and every minute
  useEffect(() => {
    if (!dateRange) {
      setTodayPosition(null);
      return;
    }
    
    const updateTodayPosition = () => {
      const now = new Date();
      const pos = calculatePosition(now, dateRange.startDate, dateRange.totalDays);
      setTodayPosition(pos >= 0 && pos <= 100 ? pos : null);
    };
    
    // Initial update
    updateTodayPosition();
    
    // Update every minute (60000ms)
    const intervalId = setInterval(updateTodayPosition, 60000);
    
    return () => clearInterval(intervalId);
  }, [dateRange]);

  // Function to scroll to today
  const scrollToToday = useCallback(() => {
    if (!dateRange || !chartRef.current) return;
    
    const today = new Date();
    const pos = calculatePosition(today, dateRange.startDate, dateRange.totalDays);
    
    if (pos >= 0 && pos <= 100) {
      // Scroll to center today in view
      const scrollContainer = chartRef.current;
      const timelineWidth = scrollContainer.scrollWidth - 200; // Subtract sidebar width
      const scrollTo = (pos / 100) * timelineWidth - (scrollContainer.clientWidth / 2) + 200;
      scrollContainer.scrollLeft = Math.max(0, scrollTo);
    }
  }, [dateRange, chartRef]);

  // Scroll to today on mount only (not on every dateRange change)
  const hasScrolledToTodayRef = useRef(false);
  useEffect(() => {
    // Only scroll to today once on initial mount
    if (hasScrolledToTodayRef.current) return;
    if (!dateRange || !chartRef.current) return;
    
    scrollToToday();
    hasScrolledToTodayRef.current = true;
  }, [dateRange, scrollToToday, chartRef]);

  // Listen for "go to today" event from HUD
  useEffect(() => {
    const handleGoToToday = () => {
      scrollToToday();
    };
    
    window.addEventListener('ganttGoToToday', handleGoToToday);
    return () => window.removeEventListener('ganttGoToToday', handleGoToToday);
  }, [scrollToToday]);

  return { todayPosition, scrollToToday };
}
